/**
 * La lógica de la pantalla de configuración, sin React.
 *
 * Vive aparte porque es lo único de esta pantalla que se puede equivocar en
 * silencio: convertir la lista de fuentes a JSON y de vuelta, y decidir qué
 * claves han cambiado de verdad. Un formulario que manda claves que nadie tocó
 * escribe en la base sin motivo y deja registros de auditoría falsos.
 *
 * El backend valida igual y es quien manda (SPEC-017 §2). Esto no duplica su
 * validación: solo prepara lo que se le envía.
 */

import type { Cambios, ClaveConfig, CuentaBancaria, Fuente, TipoFuente } from "../api/tipos.ts";

export const CLAVES = {
  fuentes: "RECAUDA_FUENTES",
  reenviadores: "RECAUDA_REENVIADORES",
  diasRecibidos: "RECAUDA_DIAS_RECIBIDOS",
  toleranciaPesos: "RECAUDA_TOLERANCIA_PESOS",
  metodosSinBanco: "RECAUDA_METODOS_SIN_BANCO",
  revisarDesde: "RECAUDA_REVISAR_DESDE",
  canalAviso: "RECAUDA_CANAL_AVISO",
  nivelAviso: "RECAUDA_NIVEL_AVISO",
  telegramToken: "RECAUDA_TELEGRAM_TOKEN",
  telegramChat: "RECAUDA_TELEGRAM_CHAT",
  umbral: "RECAUDA_UMBRAL",
  reglas: "RECAUDA_REGLAS",
  cuentasBancarias: "RECAUDA_CUENTAS_BANCARIAS",
} as const;

/** Lo que el backend devuelve en lugar de un secreto. Nunca se manda de vuelta. */
export const REDACTADO = "•••••";

export const TIPOS_FUENTE: { valor: TipoFuente; nombre: string; ayuda: string }[] = [
  { valor: "banco", nombre: "Banco", ayuda: "Manda alertas de movimientos en la cuenta." },
  { valor: "facturacion", nombre: "Facturación", ayuda: "Manda las facturas electrónicas." },
];

export type { Cambios };

// ─────────────────────────── fuentes ───────────────────────────

/**
 * El JSON guardado → la lista que edita la pantalla.
 *
 * Tolera basura a propósito. Si lo guardado no es una lista válida —porque lo
 * escribió una versión anterior a mano, o alguien lo tocó por consola— es mejor
 * empezar de cero que dejar la pantalla rota: aquí es justo donde se viene a
 * arreglarlo.
 */
export function leerFuentes(json: string | null | undefined): Fuente[] {
  if (!json) return [];
  let crudo: unknown;
  try {
    crudo = JSON.parse(json);
  } catch {
    return [];
  }
  if (!Array.isArray(crudo)) return [];

  return crudo.map((f) => {
    const o = (f ?? {}) as Record<string, unknown>;
    return {
      nombre: typeof o.nombre === "string" ? o.nombre : "",
      tipo: o.tipo === "facturacion" ? "facturacion" : "banco",
      remitentes: Array.isArray(o.remitentes) ? o.remitentes.map((r) => String(r)) : [],
    };
  });
}

/**
 * La lista → el JSON que espera el backend.
 *
 * Se limpia antes de serializar: espacios sobrantes y remitentes vacíos, que es
 * lo que deja una fila recién añadida y todavía sin rellenar. Mandarlos haría
 * que el backend rechazara la fuente entera por culpa de un renglón en blanco.
 */
export function escribirFuentes(fuentes: Fuente[]): string {
  const limpias = fuentes
    .map((f) => ({
      nombre: f.nombre.trim(),
      tipo: f.tipo,
      remitentes: f.remitentes.map((r) => r.trim()).filter(Boolean),
    }))
    .filter((f) => f.nombre || f.remitentes.length);

  // Cadena vacía y no "[]" cuando no queda ninguna. Con "[]" una cuenta recién
  // creada —que no tiene fuentes todavía— mostraba «1 cambio sin guardar» para
  // siempre, porque "" y "[]" no son iguales; y al pulsar Guardar el backend
  // rechazaba la lista vacía. Vacío significa «borra la clave», que es lo que de
  // verdad se quiere decir.
  return limpias.length ? JSON.stringify(limpias) : "";
}

export const fuenteVacia = (): Fuente => ({ nombre: "", tipo: "banco", remitentes: [""] });

// ─────────────────────────── cuentas bancarias (SPEC-030 §4) ───────────────────────────

/**
 * Una cuenta mientras se edita. El tipo puede estar sin elegir: una cuenta que
 * se agrega desde «llegaron pagos a la *1592» no dice si es de Ahorros o
 * Corriente, y suponerlo sería meter un dato que nadie dio.
 */
export interface CuentaEditable {
  banco: string;
  tipo: CuentaBancaria["tipo"] | "";
  /** Ahorros y Corriente: los últimos cuatro dígitos. */
  ultimos: string;
  /** Llave: la llave misma. */
  llave: string;
  nombre: string;
}

/** Los tipos, con lo que cada uno pide. */
export const TIPOS_CUENTA: { valor: CuentaBancaria["tipo"]; nombre: string }[] = [
  { valor: "Ahorros", nombre: "Ahorros" },
  { valor: "Corriente", nombre: "Corriente" },
  { valor: "Llave", nombre: "Llave" },
];

/**
 * Bancos que se ofrecen al escribir, además de los que ya mandan alertas. Es
 * ayuda para no equivocarse al teclear, no una lista cerrada: el banco es libre
 * (SPEC-030 §4 bis).
 */
export const BANCOS_COMUNES = ["Bancolombia", "Nu", "Davivienda", "Nequi", "Daviplata", "Bancolombia Ahorro a la mano", "Banco de Bogotá", "BBVA", "Banco Popular", "Banco Caja Social", "Banco AV Villas", "Itaú", "Scotiabank Colpatria", "Lulo Bank"];

/** El JSON guardado → la lista que edita la pantalla. Tolera basura, como `leerFuentes`. */
export function leerCuentas(json: string | null | undefined): CuentaEditable[] {
  if (!json) return [];
  let crudo: unknown;
  try {
    crudo = JSON.parse(json);
  } catch {
    return [];
  }
  if (!Array.isArray(crudo)) return [];
  return crudo.map((c) => {
    const o = (c ?? {}) as Record<string, unknown>;
    const tipo = o.tipo === "Ahorros" || o.tipo === "Corriente" || o.tipo === "Llave" ? o.tipo : "";
    return {
      banco: typeof o.banco === "string" ? o.banco : "",
      tipo,
      ultimos: typeof o.ultimos === "string" ? o.ultimos : String(o.ultimos ?? ""),
      llave: typeof o.llave === "string" ? o.llave : "",
      nombre: typeof o.nombre === "string" ? o.nombre : "",
    };
  });
}

/**
 * La lista → el JSON que espera el backend. Las filas en blanco no se mandan;
 * las a medio llenar sí, para que el backend diga qué les falta.
 */
export function escribirCuentas(cuentas: CuentaEditable[]): string {
  const limpias = cuentas
    .map((c) => ({
      banco: c.banco.trim(),
      tipo: c.tipo,
      // Una Llave lleva la llave y no los dígitos, y al revés: mandar los dos
      // dejaría guardado un dato que la cuenta no usa.
      ...(c.tipo === "Llave" ? { llave: c.llave.trim() } : { ultimos: c.ultimos.trim() }),
      nombre: c.nombre.trim(),
    }))
    .filter((c) => ("llave" in c ? c.llave : c.ultimos) || c.nombre);
  return limpias.length ? JSON.stringify(limpias) : "";
}

export const cuentaVacia = (banco = "", ultimos = ""): CuentaEditable => ({ banco, tipo: "", ultimos, llave: "", nombre: "" });

// ─────────────────────────── qué cambió ───────────────────────────

/**
 * Compara lo editado con lo guardado y devuelve SOLO lo que cambió.
 *
 * Tres reglas que salen de SPEC-017 §8:
 *
 *  - Vaciar un campo manda `null`, que borra la clave. No manda cadena vacía:
 *    una clave presente y vacía no es lo mismo que ausente, y el núcleo trata
 *    "definida pero vacía" como un error de configuración.
 *  - El valor REDACTADO nunca se manda: es lo que el `GET` devuelve en lugar de
 *    un secreto, y reenviarlo guardaría los asteriscos como si fueran el token.
 *  - Lo que no cambió no se manda. Una petición que escribe lo mismo deja un
 *    registro de auditoría que dice que alguien cambió algo cuando no lo hizo.
 */
export function calcularCambios(
  guardado: Record<string, string>,
  editado: Record<string, string>,
): Cambios {
  const cambios: Cambios = {};

  for (const [clave, valor] of Object.entries(editado)) {
    if (valor === REDACTADO) continue;

    const antes = guardado[clave] ?? "";
    const ahora = valor.trim();
    if (ahora === antes.trim()) continue;

    cambios[clave as ClaveConfig] = ahora === "" ? null : ahora;
  }

  return cambios;
}

export const hayCambios = (cambios: Cambios): boolean => Object.keys(cambios).length > 0;

/**
 * ¿Este cambio merece enseñar sus consecuencias antes de aplicarlo?
 *
 * Solo donde hay algo que explicar (SPEC-017 §5): subir el nivel de aviso hace
 * que los montos y los nombres viajen a un chat donde no hay roles, y encender
 * una regla hace que el sistema resuelva solo casos que antes preguntaba. Pedir
 * confirmación para una tolerancia de pesos sería un paso de más.
 */
export function exigeConfirmacion(cambios: Cambios): boolean {
  return (
    cambios[CLAVES.nivelAviso] === "completo" ||
    (CLAVES.reglas in cambios && !!cambios[CLAVES.reglas])
  );
}
